/**
 * batch-68 gespreksvoorbereiding (FO-346 §3.3/§3.4) - "bon ontbreekt" en "bon
 * wijkt af van planning" per kalenderdag.
 *
 * De detectoren zelf zijn gedeeld met de verloningscontrole (FO-342 regels 1
 * en 3; FO-346 §2 "gedeeld, niet gedupliceerd"): `registratie-afwijkingen.ts`.
 * Hier staat alleen wat de gespreksvoorbereiding anders doet: één regel per
 * kalenderdag (meerdere werkbonnen van die dag samen), de invuldeadline in
 * plaats van "vóór vandaag", en de eigen drempel uit de HRM-instellingen
 * (FO-346 §6).
 *
 * Puur: geen database, geen klok. De aanroeper levert per kalenderdag de
 * dagregels van de medewerker aan.
 */

import { round2 } from './leave-calculations'
import {
  dagregelZonderRegistratie,
  planningVergelijking,
  wijktAfVanPlanning,
  type PlanningVergelijking,
  type RegistratieDagregel,
} from './registratie-afwijkingen'

/** Eén kalenderdag van een medewerker: de werkbondagen van die dag samen. */
export interface GvRegistratieDag {
  /** NL-kalenderdag yyyy-mm-dd. */
  datum: string
  /** Ids van de dagregels van deze dag (voor de doorklik). */
  dayEntryIds: string[]
  /** De werkbon van de eerste dagregel (doorklik naar de bron). */
  werkbonId: string
  /** Minstens één geplande dagregel zonder registratie (gedeelde detector, FO-342 regel 1). */
  zonderRegistratie: boolean
  /** Planning tegenover registratie (gedeelde detector, FO-342 regel 3); null = niets te vergelijken. */
  planning: PlanningVergelijking | null
  /**
   * Som van de geplande uren (na pauze) van de niet-afwezige dagregels met
   * urenplicht (zonder consignatienacht), of null als een planning ontbreekt.
   */
  geplandeUren: number | null
}

/** Een kalenderdag uit zijn dagregels (alle werkbonnen van één medewerker op die dag). */
export function gvRegistratieDag(dag: {
  datum: string
  dayEntryIds: string[]
  werkbonId: string
  regels: ReadonlyArray<RegistratieDagregel>
}): GvRegistratieDag {
  // Een piketnacht naast een dagdienst telt niet mee in "gepland" van de dag.
  const actief = dag.regels.filter((r) => !r.isAfwezig && !r.geenUrenplicht)
  const gepland =
    actief.length > 0 && actief.every((r) => r.geplandeUren != null)
      ? round2(actief.reduce((s, r) => s + (r.geplandeUren ?? 0), 0))
      : null
  return {
    datum: dag.datum,
    dayEntryIds: dag.dayEntryIds,
    werkbonId: dag.werkbonId,
    zonderRegistratie: dag.regels.some(dagregelZonderRegistratie),
    planning: planningVergelijking(dag.regels),
    geplandeUren: gepland,
  }
}

/**
 * "Bon ontbreekt": een geplande werkdag met een dagregel zonder registratie,
 * zonder afwezigheid, waarvan de invuldeadline voorbij is. Een consignatienacht
 * (piket) telt nooit: daar geldt geen urenplicht per dag.
 */
export function gvOntbrekendeBonnen(
  dagen: ReadonlyArray<GvRegistratieDag>,
  opties: {
    nu: Date
    /** Einde van de invuldeadline van een werkdag (UTC-instant). */
    deadlineEindeVan: (datum: string) => Date
    /** Afwezigheid uit een andere bron (ziekte, goedgekeurd verlof) op die dag. */
    heeftAfwezigheid: (datum: string) => boolean
  },
): GvRegistratieDag[] {
  return dagen.filter(
    (d) =>
      d.zonderRegistratie &&
      !opties.heeftAfwezigheid(d.datum) &&
      opties.deadlineEindeVan(d.datum).getTime() < opties.nu.getTime(),
  )
}

/**
 * "Bon wijkt af van planning": dagen waarop |geregistreerd - gepland| groter is
 * dan `drempelUren` (de HRM-drempel, FO-346 §6).
 */
export function gvAfwijkingenVanPlanning(
  dagen: ReadonlyArray<GvRegistratieDag>,
  drempelUren: number,
): Array<{ dag: GvRegistratieDag; verschilUren: number }> {
  const uit: Array<{ dag: GvRegistratieDag; verschilUren: number }> = []
  for (const d of dagen) {
    if (d.planning && wijktAfVanPlanning(d.planning.verschil, drempelUren)) {
      uit.push({ dag: d, verschilUren: d.planning.verschil })
    }
  }
  return uit
}
